// MasterRotationModal — fullscreen-ish progress overlay shown while a
// master-password rotation is in flight.
//
// Pure render of a parent-supplied `RotationProgress`. Non-dismissable
// while rotation is in flight; Close is enabled on both `done` and
// `aborted` terminal phases. The Close button triggers the
// parent-supplied `onClose` (the parent is responsible for tearing
// down its rotation state).
//
// Sized for both desktop and the tightest mobile viewports — same
// `mx-auto max-w-md` envelope used by the lifecycle screens.

import type { RotationProgress, RotationResultRow } from "../../lib/masterRotation";

export interface MasterRotationModalProps {
  readonly progress: RotationProgress;
  readonly onClose: () => void;
}

export function MasterRotationModal({
  progress,
  onClose,
}: MasterRotationModalProps) {
  const isDone = progress.phase.kind === "done";
  const isAborted = progress.phase.kind === "aborted";
  const showClose = isDone || isAborted;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Master password rotation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-bg-raised p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-medium tracking-tight">
          Changing master password
        </h2>
        <PhaseBody progress={progress} />
        <ResultsList results={progress.results} />
        {showClose && (
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PhaseBody({ progress }: { progress: RotationProgress }) {
  const { phase, results } = progress;
  if (phase.kind === "verifying-current") {
    return (
      <BusyRow text="Verifying current password…" />
    );
  }
  if (phase.kind === "rotating-vault") {
    return (
      <BusyRow
        text={`Rotating: ${phase.displayName} (${phase.index + 1} of ${phase.total})`}
      />
    );
  }
  if (phase.kind === "rotating-master") {
    return <BusyRow text="Rotating master handle…" />;
  }
  if (phase.kind === "saving-config") {
    return <BusyRow text="Saving encrypted config…" />;
  }
  if (phase.kind === "aborted") {
    return <AbortedBody reason={phase.reason} error={phase.error} />;
  }
  // done
  const rotatedCount = results.filter((r) => r.outcome === "success").length;
  const pendingCount = results.filter((r) => r.outcome === "failure").length;
  if (pendingCount === 0) {
    return (
      <p className="text-sm text-text">
        Master password updated. {rotatedCount}{" "}
        {rotatedCount === 1 ? "vault" : "vaults"} rotated.
      </p>
    );
  }
  return (
    <p className="text-sm text-text">
      {rotatedCount}{" "}
      {rotatedCount === 1 ? "vault" : "vaults"} updated. {pendingCount}{" "}
      pending retry.
    </p>
  );
}

function AbortedBody({
  reason,
  error,
}: {
  reason: "wrong-current-master" | "master-rotation-failed" | "save-config-failed";
  error: string;
}) {
  let headline: string;
  let guidance: string;
  if (reason === "wrong-current-master") {
    headline = "Current master was wrong.";
    guidance = "Check the current master and try again.";
  } else if (reason === "master-rotation-failed") {
    headline = "Master rotation failed.";
    guidance =
      "Some vaults may have already rotated — see the Vaults tab to retry the rest.";
  } else {
    headline = "Encrypted config could not be saved.";
    guidance = "Retry from the Master password tab, or reload and try again.";
  }
  return (
    <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm">
      <div className="mb-1 font-medium text-danger">{headline}</div>
      <p className="mb-2 text-[13px] leading-relaxed text-text">{guidance}</p>
      <p className="font-mono text-[12px] text-text-soft">{error}</p>
    </div>
  );
}

function BusyRow({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3 text-sm text-text-soft">
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      <span>{text}</span>
    </div>
  );
}

function ResultsList({ results }: { results: RotationResultRow[] }) {
  if (results.length === 0) return null;
  return (
    <ul className="mt-5 space-y-1.5 text-[13px]">
      {results.map((r) => (
        <li
          key={r.vaultId}
          className="flex items-start justify-between gap-3 border-t border-border pt-1.5"
        >
          <span className="font-mono">{r.displayName}</span>
          <ResultBadge row={r} />
        </li>
      ))}
    </ul>
  );
}

function ResultBadge({ row }: { row: RotationResultRow }) {
  if (row.outcome === "success") {
    return <span className="text-ok">✓</span>;
  }
  if (row.outcome === "failure") {
    return (
      <span className="text-right text-danger">
        × {row.error ?? "failed"}
      </span>
    );
  }
  return <span className="text-text-dim">…</span>;
}
